import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../components/Header";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MapPin, Star, Trash2, ArrowRight, Compass } from "lucide-react";
import { useFavorites } from "../../features/useFavorites";

const G       = "#3A9D23";
const G_DARK  = "#2f7d1c";
const G_LIGHT = "#f0fdf4";
const API     = "https://api.komorotel.com";

const TYPE_LABELS = {
  hotel:               "Hôtel",
  chambre_hote:        "Chambre d'hôte",
  appartement_studio:  "Appartement / Studio",
};

const ISLAND_COLORS = {
  "Grande Comore": { bg: "#f0fdf4", color: G        },
  "Anjouan":       { bg: "#eff6ff", color: "#2563eb" },
  "Mohéli":        { bg: "#fff7ed", color: "#ea580c" },
};

export default function Favorites() {
  const { favorites, toggleFavorite, count } = useFavorites();
  const [hotels,  setHotels]  = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch tous les hôtels favoris en parallèle
  useEffect(() => {
    if (favorites.length === 0) { setLoading(false); setHotels([]); return; }
    setLoading(true);
    Promise.allSettled(
      favorites.map(id => axios.get(`${API}/api/hotels/${id}`))
    ).then(results => {
      const found = results
        .filter(r => r.status === "fulfilled")
        .map(r => r.value.data);
      setHotels(found);
    }).finally(() => setLoading(false));
  }, [favorites.join(",")]);

  const handleRemove = (hotelId) => {
    toggleFavorite(hotelId);
    setHotels(prev => prev.filter(h => h._id !== hotelId));
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=Playfair+Display:wght@600;700&display=swap');
        @keyframes shimmer { to { background-position: -200% 0; } }
        @keyframes heartPop { 0%{transform:scale(1)} 40%{transform:scale(1.35)} 70%{transform:scale(0.9)} 100%{transform:scale(1)} }

        .fav-card {
          background: #fff;
          border-radius: 20px;
          border: 1px solid #efefef;
          overflow: hidden;
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .fav-card:hover { box-shadow: 0 12px 40px rgba(0,0,0,0.09); transform: translateY(-2px); }
        .fav-card:hover .fav-img { transform: scale(1.06); }

        .fav-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94); }

        .fav-remove-btn {
          position: absolute; top: 10px; right: 10px;
          width: 34px; height: 34px; border-radius: 50%;
          background: rgba(255,255,255,0.92);
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.12);
          transition: background 0.18s, transform 0.18s;
        }
        .fav-remove-btn:hover { background: #fef2f2; transform: scale(1.1); }

        .fav-cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: ${G}; color: #fff;
          border: none; border-radius: 12px;
          padding: 12px 22px; font-size: 14px; font-weight: 700;
          cursor: pointer; font-family: inherit;
          transition: background 0.18s;
          text-decoration: none;
        }
        .fav-cta-btn:hover { background: ${G_DARK}; }

        @media (max-width: 640px) {
          .fav-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <Header />

      <div style={{ minHeight: "100vh", background: "#fafaf9", fontFamily: "'DM Sans', system-ui, sans-serif", paddingTop: 96, paddingBottom: 80 }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px" }}>

          {/* ── En-tête ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={{ marginBottom: 36 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
              <div style={{ width: 4, height: 28, borderRadius: 99, background: G }} />
              <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px,5vw,30px)", fontWeight: 700, color: "#111827", margin: 0 }}>
                Mes favoris
              </h1>
            </div>
            <p style={{ fontSize: 13, color: "#9ca3af", marginLeft: 16, fontStyle: "italic" }}>
              {count > 0 ? `${count} hébergement${count > 1 ? "s" : ""} sauvegardé${count > 1 ? "s" : ""}` : "Aucun hébergement sauvegardé"}
            </p>
          </motion.div>

          {/* ── Skeleton ── */}
          {loading && (
            <div className="fav-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ height: 280, borderRadius: 20, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: `shimmer 1.4s ${i * 0.1}s infinite` }} />
              ))}
            </div>
          )}

          {/* ── Vide ── */}
          {!loading && hotels.length === 0 && (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
              style={{ background: "#fff", borderRadius: 24, border: "1px dashed #e5e7eb", padding: "72px 24px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, background: "#fef2f2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <Heart size={30} color="#fca5a5" />
              </div>
              <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                Aucun favori pour l'instant
              </p>
              <p style={{ fontSize: 14, color: "#9ca3af", marginBottom: 28, maxWidth: 320, margin: "0 auto 28px" }}>
                Explorez nos hébergements et cliquez sur le cœur pour sauvegarder vos coups de cœur.
              </p>
              <Link to="/hotels" className="fav-cta-btn">
                <Compass size={16} /> Explorer les hébergements
              </Link>
            </motion.div>
          )}

          {/* ── Grille ── */}
          {!loading && hotels.length > 0 && (
            <div className="fav-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
              <AnimatePresence>
                {hotels.map((hotel, i) => {
                  const islandStyle = ISLAND_COLORS[hotel.island] || { bg: "#f9fafb", color: "#6b7280" };
                  return (
                    <motion.div key={hotel._id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                      transition={{ delay: i * 0.06, duration: 0.35 }}
                      className="fav-card">

                      {/* Image */}
                      <div style={{ position: "relative", height: 200, overflow: "hidden", background: "#f3f4f6" }}>
                        {hotel.photos?.[0]
                          ? <img className="fav-img" src={hotel.photos[0]} alt={hotel.name} />
                          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Compass size={32} color="#d1d5db" /></div>
                        }

                        {/* Gradient bas */}
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80, background: "linear-gradient(transparent, rgba(0,0,0,0.45))" }} />

                        {/* Type badge */}
                        <div style={{ position: "absolute", top: 12, left: 12, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)", borderRadius: 8, padding: "4px 10px" }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#fff" }}>{TYPE_LABELS[hotel.type] || hotel.type}</span>
                        </div>

                        {/* Bouton supprimer */}
                        <button className="fav-remove-btn" onClick={() => handleRemove(hotel._id)} title="Retirer des favoris">
                          <Heart size={15} fill="#ef4444" color="#ef4444" />
                        </button>

                        {/* Prix en bas de l'image */}
                        {hotel.minPrice && (
                          <div style={{ position: "absolute", bottom: 12, left: 12, display: "flex", alignItems: "baseline", gap: 3 }}>
                            <span style={{ fontSize: 16, fontWeight: 800, color: "#fff", fontFamily: "'Playfair Display', Georgia, serif" }}>{hotel.minPrice}€</span>
                            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>/ nuit</span>
                          </div>
                        )}
                      </div>

                      {/* Infos */}
                      <div style={{ padding: "16px 18px 18px" }}>
                        {/* Île */}
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, background: islandStyle.bg, color: islandStyle.color, padding: "3px 10px", borderRadius: 99 }}>
                            {hotel.island}
                          </span>
                        </div>

                        <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 6px", lineHeight: 1.3 }}>
                          {hotel.name}
                        </h3>

                        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
                          <MapPin size={12} color="#9ca3af" />
                          <span>{hotel.city}</span>
                        </div>

                        {/* Rating si disponible */}
                        {hotel.rating > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 14 }}>
                            <Star size={13} fill="#f59e0b" color="#f59e0b" />
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{hotel.rating.toFixed(1)}</span>
                            {hotel.reviewCount > 0 && <span style={{ fontSize: 12, color: "#9ca3af" }}>({hotel.reviewCount} avis)</span>}
                          </div>
                        )}

                        {/* CTA */}
                        <Link
                          to={`/hotel/${hotel.slug}`}
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                            background: G_LIGHT, color: G, border: `1.5px solid ${G}30`,
                            borderRadius: 10, padding: "9px 0", fontSize: 13, fontWeight: 700,
                            textDecoration: "none", transition: "all 0.18s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = G; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = G_LIGHT; e.currentTarget.style.color = G; }}>
                          Voir l'hébergement <ArrowRight size={14} />
                        </Link>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          {/* ── Explorer plus ── */}
          {!loading && hotels.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              style={{ marginTop: 32, textAlign: "center" }}>
              <Link to="/hotels" className="fav-cta-btn">
                <Compass size={16} /> Explorer d'autres hébergements
              </Link>
            </motion.div>
          )}

        </div>
      </div>
    </>
  );
}